import React, { useRef, useCallback, useState } from 'react';
import { Camera, X, AlertCircle, CheckCircle } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
// api import kaldırıldı; bu komponent sadece frontend tarafında dosya yönetimi yapıyor

const BaskiGorselUpload = ({ 
  images = [], // Parent'tan gelen görsel listesi
  onImagesChange, // Parent'a değişiklikleri bildiren callback
  onImagesUpload, // Alternatif callback (geriye uyumluluk için)
  maxFiles = null, // null ise sınırsız
  maxSize = 10 * 1024 * 1024,
  acceptedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'],
  className = ''
}) => {
  const [isPasteMode] = useState(true);
  const fileInputRef = useRef(null);

  // Dosya yükleme işlemi
  const handleFiles = useCallback((files) => {
    const validFiles = Array.from(files).filter(file => {
      if (!acceptedTypes.includes(file.type)) {
        alert(`${file.name} desteklenmeyen dosya formatı`);
        return false;
      }
      if (file.size > maxSize) {
        alert(`${file.name} dosya boyutu çok büyük (max: ${maxSize / 1024 / 1024}MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    // Maksimum dosya sayısı kontrolü (sadece maxFiles belirtilmişse)
    if (maxFiles !== null && maxFiles !== undefined && images.length + validFiles.length > maxFiles) {
      alert(`Maksimum ${maxFiles} dosya yükleyebilirsiniz`);
      return;
    }

    // Yeni görselleri ekle
    const newImages = validFiles.map((file, index) => ({
      id: `new_${Date.now()}_${index}`,
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      status: 'success'
    }));

    if (onImagesChange) {
      onImagesChange([...images, ...newImages]);
    } else if (onImagesUpload) {
      onImagesUpload(validFiles);
    }
  }, [acceptedTypes, maxSize, maxFiles, images, onImagesChange, onImagesUpload]);

  // Dropzone konfigürasyonu
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFiles,
    accept: acceptedTypes.reduce((acc, type) => ({ ...acc, [type]: [] }), {}),
    maxSize,
    multiple: true,
    noClick: true
  });

  // Dosya seçme
  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  // Yapıştır (paste) işlemi
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const items = e.clipboardData?.items;
    if (!items) return;

    const files = [];
    for (let item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }

    if (files.length > 0) {
      handleFiles(files);
    }
  }, [handleFiles]);

  // Global paste event listener
  React.useEffect(() => {
    const handleGlobalPaste = (e) => {
      if (isPasteMode) {
        handlePaste(e);
      }
    };

    document.addEventListener('paste', handleGlobalPaste);
    return () => document.removeEventListener('paste', handleGlobalPaste);
  }, [isPasteMode, handlePaste]);

  // Görsel silme
  const handleRemoveImage = (imageId) => {
    const imageToRemove = images.find(img => img.id === imageId);
    const newImages = images.filter(img => img.id !== imageId);
    
    // Eğer File objesi varsa URL'ini temizle
    if (imageToRemove && imageToRemove.file instanceof File && imageToRemove.preview) {
      URL.revokeObjectURL(imageToRemove.preview);
    }
    
    if (onImagesChange) {
      onImagesChange(newImages);
    }
  };

  return (
    <div 
      {...getRootProps()}
      className={`space-y-4 ${className}`}
    >
      <input {...getInputProps()} ref={fileInputRef} />
      
      {/* Görseller */}
      <div className="space-y-2">
        {images.length > 0 && (
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Görseller ({images.length})
          </h4>
        )}
        
        <div className="flex flex-wrap gap-2 sm:gap-4">
          {/* Yüklenen görseller */}
          {images.map((image) => (
            <div key={image.id} className="relative group">
              <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 w-20 h-20 sm:w-24 sm:h-24">
                <img
                  src={image.preview}
                  alt="Görsel"
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity rounded-lg flex items-center justify-center">
                <button
                  onClick={() => handleRemoveImage(image.id)}
                  className="opacity-0 group-hover:opacity-100 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
              
              {/* Status */}
              <div className="absolute top-1 right-1">
                {image.status === 'uploading' && (
                  <div className="w-3 h-3 sm:w-4 sm:h-4 bg-yellow-500 rounded-full flex items-center justify-center">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                {image.status === 'success' && (
                  <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-500" />
                )}
                {image.status === 'error' && (
                  <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4 text-red-500" />
                )}
              </div>
            </div>
          ))}
          
          {/* Yeni görsel ekleme kutusu */}
          {(maxFiles === null || maxFiles === undefined || images.length < maxFiles) && (
            <div
              onClick={handleFileSelect}
              className="aspect-square w-20 h-20 sm:w-24 sm:h-24 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-gray-50 dark:bg-gray-800"
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={acceptedTypes.join(',')}
                onChange={(e) => handleFiles(e.target.files)}
                className="hidden"
              />
              <Camera className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500 dark:text-purple-400" />
              <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">+</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BaskiGorselUpload;